import React, { useEffect, useMemo, useState } from "react";
import { fechaHoraMX } from "@/lib/format";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { format } from "date-fns";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft } from "lucide-react";
import ReporteModulo from "@/components/ReporteModulo";

L.Marker.prototype.options.icon = L.icon({
  iconUrl: icon, shadowUrl: iconShadow,
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

const parseDispositivo = (ua) => {
  if (!ua) return "—";
  let os = "Desconocido";
  if (/Windows/i.test(ua)) os = "Windows";
  else if (/iPhone|iPad|iOS/i.test(ua)) os = "iOS";
  else if (/Android/i.test(ua)) os = "Android";
  else if (/Mac OS X|Macintosh/i.test(ua)) os = "macOS";
  else if (/Linux/i.test(ua)) os = "Linux";
  let br = "";
  if (/Edg\//i.test(ua)) br = "Edge";
  else if (/OPR\/|Opera/i.test(ua)) br = "Opera";
  else if (/Chrome\//i.test(ua)) br = "Chrome";
  else if (/Firefox\//i.test(ua)) br = "Firefox";
  else if (/Safari\//i.test(ua)) br = "Safari";
  return br ? `${os} · ${br}` : os;
};

const fmtFecha = (iso) => {
  if (!iso) return "—";
  return fechaHoraMX(iso);
};

const ubic = (a) => [a.city, a.region, a.country].filter(Boolean).join(", ") || "—";

export default function BitacoraAccesos() {
  const navigate = useNavigate();
  const [accesos, setAccesos] = useState([]);
  const [userSel, setUserSel] = useState("todos");
  const [resSel, setResSel] = useState("todos");
  const [periodoSel, setPeriodoSel] = useState("todos");

  useEffect(() => {
    api.get("/accesos").then(({ data }) => setAccesos(data || [])).catch(() => {});
  }, []);

  const enPeriodo = (iso) => {
    if (periodoSel === "todos") return true;
    if (!iso) return false;
    const d = new Date(iso);
    const now = new Date();
    const sameYear = d.getFullYear() === now.getFullYear();
    if (periodoSel === "hoy") return d.toDateString() === now.toDateString();
    if (periodoSel === "mes") return sameYear && d.getMonth() === now.getMonth();
    if (periodoSel === "meses") return sameYear && d.getMonth() < now.getMonth();
    if (periodoSel === "anios") return d.getFullYear() < now.getFullYear();
    return true;
  };

  const usuarios = useMemo(
    () => Array.from(new Set(accesos.map((a) => a.username).filter(Boolean))).sort(),
    [accesos]
  );
  const filtrados = useMemo(
    () => accesos.filter((a) =>
      (userSel === "todos" || a.username === userSel) &&
      (resSel === "todos" || (resSel === "exito" ? a.exito : !a.exito)) &&
      enPeriodo(a.created_at)
    ),
    [accesos, userSel, resSel, periodoSel]
  );

  const puntos = useMemo(
    () => filtrados.filter((a) => a.lat != null && a.lng != null).map((a) => ({ ...a, lat: Number(a.lat), lng: Number(a.lng) })),
    [filtrados]
  );
  const center = puntos.length ? [puntos[0].lat, puntos[0].lng] : [23.63, -102.55]; // México

  const columns = [
    { header: "Usuario", value: (r) => r.username || "—" },
    { header: "Fecha y hora", value: (r) => fmtFecha(r.created_at) },
    { header: "Resultado", value: (r) => (r.exito ? "Éxito" : "Fallido") },
    { header: "IP", value: (r) => r.ip || "—", mono: true },
    { header: "Ubicación", value: (r) => ubic(r) },
    { header: "Dispositivo", value: (r) => parseDispositivo(r.user_agent) },
  ];

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="bitacora-page">
      <Button variant="ghost" size="sm" onClick={() => navigate("/configuracion")}><ArrowLeft className="h-4 w-4 mr-2" /> Configuración</Button>
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Bitácora de accesos</h1>
          <p className="text-muted-foreground mt-2">Inicios de sesión (exitosos y fallidos), con IP, ubicación aproximada y dispositivo.</p>
        </div>
        <ReporteModulo
          titulo="Bitácora de accesos"
          filenameBase="Bitacora de accesos"
          rows={filtrados}
          getFecha={(r) => (r.created_at || "").slice(0, 10)}
          groupBy={(r) => r.username || "—"}
          filtros={[
            { label: "Usuario", value: userSel === "todos" ? "" : userSel },
            { label: "Resultado", value: resSel === "todos" ? "" : resSel === "exito" ? "Solo exitosos" : "Solo fallidos" },
            { label: "Periodo", value: { todos: "", hoy: "Accesos del día", mes: "Mes en curso", meses: "Meses anteriores", anios: "Años anteriores" }[periodoSel] },
          ]}
          columns={columns}
          triggerTestId="accesos-reporte-btn"
          prefix="accesos-rep"
        />
      </div>

      {puntos.length > 0 && (
        <Card data-testid="accesos-mapa">
          <CardHeader><CardTitle className="text-xl">Ubicación aproximada de accesos</CardTitle></CardHeader>
          <CardContent>
            <MapContainer center={center} zoom={5} className="h-[420px] w-full rounded-md z-0">
              <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" maxZoom={19} />
              {puntos.map((a) => (
                <Marker key={a.id} position={[a.lat, a.lng]}>
                  <Popup>
                    <b>{a.username}</b><br />
                    {ubic(a)}<br />
                    {fmtFecha(a.created_at)}<br />
                    {a.ip} · {a.exito ? "Éxito" : "Fallido"}
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="gap-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="text-xl">Registros ({filtrados.length})</CardTitle>
            <div className="flex flex-wrap gap-2">
              <Select value={periodoSel} onValueChange={setPeriodoSel}>
                <SelectTrigger className="w-48" data-testid="accesos-periodo-filter"><SelectValue placeholder="Periodo" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos los periodos</SelectItem>
                  <SelectItem value="hoy">Accesos del día</SelectItem>
                  <SelectItem value="mes">Mes en curso</SelectItem>
                  <SelectItem value="meses">Meses anteriores</SelectItem>
                  <SelectItem value="anios">Años anteriores</SelectItem>
                </SelectContent>
              </Select>
              <Select value={resSel} onValueChange={setResSel}>
                <SelectTrigger className="w-40" data-testid="accesos-res-filter"><SelectValue placeholder="Resultado" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="exito">Solo exitosos</SelectItem>
                  <SelectItem value="fallido">Solo fallidos</SelectItem>
                </SelectContent>
              </Select>
              <Select value={userSel} onValueChange={setUserSel}>
                <SelectTrigger className="w-52" data-testid="accesos-user-filter"><SelectValue placeholder="Usuario" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos los usuarios</SelectItem>
                  {usuarios.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="border rounded-md overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Usuario</TableHead>
                  <TableHead>Fecha y hora</TableHead>
                  <TableHead>Resultado</TableHead>
                  <TableHead>IP</TableHead>
                  <TableHead>Ubicación</TableHead>
                  <TableHead>Dispositivo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtrados.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Sin registros de acceso todavía.</TableCell></TableRow>
                ) : filtrados.map((a) => (
                  <TableRow key={a.id} data-testid={`acceso-row-${a.id}`} className={a.exito ? "" : "bg-red-50"}>
                    <TableCell className="font-medium">{a.username || "—"}</TableCell>
                    <TableCell className="whitespace-nowrap">{fmtFecha(a.created_at)}</TableCell>
                    <TableCell><Badge variant={a.exito ? "default" : "destructive"}>{a.exito ? "Éxito" : "Fallido"}</Badge></TableCell>
                    <TableCell className="font-mono text-xs">{a.ip || "—"}</TableCell>
                    <TableCell className="text-sm">{ubic(a)}</TableCell>
                    <TableCell>{parseDispositivo(a.user_agent)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
